import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import DialogChatAPI from "../../../api/dialogChatApi";
import SpecialistApi from "../../../api/specialistsApi";
import { ReactComponent as ScrapIcon } from "../../../assets/Chat/scrap.svg";
import Loader from "../../../components/Loader/Loader";
import MultipleSelect from "../../../components/MultipleSelect/MultipleSelect";
import StandartButton from "../../../components/StandartButton/StandartButton";
import Tabs from "../../../components/Tabs/Tabs";
import { ISO639Lang } from "../../../constans/languagesList";
import { emptyMultiLangStrings } from "../../../constans/siteLangs";
import { ContextProvider } from "../../../contextProvider";
import { TypeSpecialistFilter } from "../../../types/TypeSpecialist";
import { getTranslation } from "../../../utils/getTranslation";
import { SuccesNotify } from "../../../utils/toaster";
import useAutosizeTextArea from "../../../utils/useAutosizeTextArea";
import FilePreview from "../Chat/FilePreview";
import chatStyles from "../GlobalChat.module.css";
import s from "./MassMailing.module.css";

type MassMailingProps = {
  setModal: Dispatch<SetStateAction<boolean>>;
  onSent?: () => void;
};

const MassMailing = ({
  setModal,
  onSent,
}: MassMailingProps) => {
  const { userData } = useContext(ContextProvider);
  const token = localStorage.getItem("token");
  const [newMailing, setNewMailing] = useState<{
    filters: TypeSpecialistFilter;
    labels: { text: string; language: string }[];
    files: File[];
  }>({
    filters: {},
    labels: emptyMultiLangStrings,
    files: [],
  });
  const [selectedTextLang, setSelectedTextLang] = useState("en");
  const [filtersData, setFiltersData] = useState<{
    mainSpecializations: { lable: string; value: string; id: string }[];
    levels: { lable: string; value: string }[];
  }>({
    mainSpecializations: [],
    levels: [{ lable: "All levels", value: "all" }],
  });
  const [loaders, setLoaders] = useState({ sendMessages: false });
  const [mailingIsStart, setMailingIsStart] = useState(false);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(
    textAreaRef.current,
    getTranslation(newMailing.labels, selectedTextLang)
  );

  useEffect(() => {
    (async () => {
      if (!token) return;
      const response = await SpecialistApi.getMainSpecializations(token);
      if (response.status && response.mainSpecializations) {
        const specs = [
          { lable: "All specializations", value: "all", id: "all" },
          ...response.mainSpecializations.map((spec) => ({
            lable: getTranslation(spec.labels, "en"),
            value: spec.specialization,
            id: spec._id,
          })),
        ];
        setFiltersData((prev) => ({ ...prev, mainSpecializations: specs }));
      }
    })();
  }, []);

  useEffect(() => {
    if (!newMailing.filters.specialistId?.mainSpecializations) return;
    (async () => {
      if (!token) return;
      const mainSpecId = filtersData.mainSpecializations.find(
        (spec) =>
          spec.value ===
          newMailing.filters.specialistId!.mainSpecializations![0]
      )?.id;

      if (!mainSpecId) return;

      const response = await SpecialistApi.getAllSpecialistLevels({
        mainSpecId,
      });

      if (response.status && response.levels) {
        const levels = [
          { lable: "All levels", value: "all" },
          ...response.levels.map((level) => ({
            lable:
              getTranslation(level.title, "en") +
              " " +
              `(${getTranslation(level.mainSpecialization.labels, "en")})`,
            value: level._id,
          })),
        ];
        setFiltersData((prev) => ({ ...prev, levels }));
      }
    })();
  }, [newMailing.filters.specialistId?.mainSpecializations]);

  const sendMessage = async () => {
    if (!token || !userData) return;

    const notEmptyLabels = newMailing.labels?.filter(
      (label) => label.text.trim() !== ""
    );

    const payload = {
      userId: userData._id,
      filters: newMailing.filters,
      message: notEmptyLabels[0]?.text || "",
      files: newMailing.files,
      labels: notEmptyLabels.length ? notEmptyLabels : undefined,
    };
    setLoaders((prev) => ({ ...prev, sendMessages: true }));
    const response = await DialogChatAPI.distributeAdminMessage(token, payload);
    setLoaders((prev) => ({ ...prev, sendMessages: false }));
    if (response.status) {
      setMailingIsStart(true);
      SuccesNotify("Message sent successfully.");
      onSent?.();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.indexOf("image") !== -1) {
        const file = item.getAsFile();
        if (file) {
          if (file.size >= 50 * 1024 * 1024) {
            alert("Image size must be less than 50MB.");
            return;
          }

          if (newMailing.files && newMailing.files.length >= 5) {
            alert("You can only select up to 5 files.");
            return;
          }

          setNewMailing((prev) => ({
            ...prev,
            files: prev.files ? [...prev.files, file] : [file],
          }));
        }
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files).filter(
        (file) => !file.type.includes("video")
      );

      if (newFiles.length + newMailing.files.length > 5) {
        alert("You can only select up to 5 files at a time.");
        e.target.value = ""; // Reset the input to allow re-selecting files
        return;
      }
      setNewMailing((prev) => ({
        ...prev,
        files: prev.files ? [...prev.files, ...newFiles] : newFiles,
      }));
    }
  };

  const langTabsDdata = emptyMultiLangStrings.map((item) => ({
    title: ISO639Lang[item.language],
    value: item.language,
  }));

  if (loaders.sendMessages) {
    return (
      <div className={s.laoderContainer}>
        <Loader size={100} />
      </div>
    );
  }

  if (mailingIsStart) {
    return (
      <div className={s.container}>
        <h3>Mass messaging has started, this may take some time</h3>
        <div className={s.actionBlock}>
          <StandartButton
            action={() => setModal(false)}
            buttonTitle={"OK"}
            width="200px"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={s.container}>
      <div className={s.filtersBlock}>
        <MultipleSelect
          data={filtersData.mainSpecializations}
          multiplie={false}
          setValue={(value) =>
            setNewMailing((prev) => ({
              ...prev,
              filters: {
                ...prev.filters,
                specialistId: {
                  ...prev.filters.specialistId,
                  mainSpecializations:
                    value === "all" ? undefined : [value as string],
                },
              },
            }))
          }
          value={
            newMailing.filters.specialistId?.mainSpecializations?.[0] ?? "all"
          }
          disabled={filtersData.mainSpecializations.length === 1}
        />
        <MultipleSelect
          data={filtersData.levels}
          multiplie={true}
          setValue={(value) => {
            setNewMailing((prev) => ({
              ...prev,
              filters: {
                ...prev.filters,
                specialistId: {
                  ...prev.filters.specialistId,
                  level:
                    (value.length === 1 && value.includes("all")) ||
                    !value.length ||
                    value[value.length - 1] === "all"
                      ? undefined
                      : ((value as string[]).filter(
                          (el) => el !== "all"
                        ) as string[]),
                },
              },
            }));
          }}
          value={newMailing.filters.specialistId?.level ?? ["all"]}
          disabled={filtersData.levels.length === 0}
          checkBox
          chip
        />
      </div>
      <div className={s.inputSection}>
        <div className={s.langTabsBlock}>
          <Tabs
            tabsData={langTabsDdata}
            activeTab={selectedTextLang}
            tabClickFunc={(value) => setSelectedTextLang(value)}
          />
        </div>
        <div className={s.chatInputBlock}>
          <textarea
            id="review-text"
            ref={textAreaRef}
            value={getTranslation(newMailing.labels, selectedTextLang, true)}
            placeholder={"Type message..."}
            className={chatStyles.chatInput}
            onPaste={handlePaste}
            onChange={(e) => {
              const text = e.target.value;
              setNewMailing((prev) => ({
                ...prev,
                labels: prev.labels.map((label) =>
                  label.language === selectedTextLang
                    ? { ...label, text }
                    : label
                ),
              }));
            }}
            rows={1}
            style={{
              resize: "none",
              overflowY: "auto",
              maxHeight: "300px",
            }}
          />

          <input
            type="file"
            id="fileInputMassMailing"
            accept="image/*, application/pdf, .txt, .doc, .docx, .xml, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <div style={{ cursor: "pointer" }}>
            <ScrapIcon
              width={25}
              height={25}
              onClick={() =>
                document.getElementById("fileInputMassMailing")?.click()
              }
            />
          </div>
        </div>
        <div className={s.filePreviewBlock}>
          {newMailing.files &&
            newMailing.files.map((file, index) => (
              <FilePreview
                key={index}
                file={file}
                onDelete={() =>
                  setNewMailing((prev) => ({
                    ...prev,
                    files: prev.files?.filter((_, i) => i !== index),
                  }))
                }
              />
            ))}
        </div>
      </div>
      <div className={s.actionBlock}>
        <StandartButton
          action={sendMessage}
          buttonTitle={"Send"}
          width="200px"
        />
      </div>
    </div>
  );
};

export default MassMailing;
